import { useEffect, useState } from "react";

import Button from "@mui/material/Button";

import AddIcon from "@mui/icons-material/Add";
import CompetitorIndication from "./components/CompetitorIndication";
import NitroService from "../../../../utils/nitro.service";
import { useNitroStore } from "../../../../store/Store";
import IIndicationHistory from "../../../../types/IIndicationHistory";

import "./index.css";
import CloseIcon from "../../../../components/CloseIcon";
import LoadingIcon from "../../../../components/Loading";
import NewIndication from "./components/NewIndication";
import { useIsComponentMounted } from "../../../../hooks/useIsComponentMounted";
import ICompAnalysis from "../../../../types/ICompAnalysis";
import { useIsMobile } from "../../../../hooks/useIsMobile";

const Competitor = ({
  competitor,
  reload,
  close,
  defaultState = "history",
  newIndication,
}: {
  competitor: ICompAnalysis;
  reload: () => void;
  close: () => void;
  defaultState?: "history" | "new";
  newIndication: {
    offer: number;
    freight: number;
    netback: number;
  } | null;
}) => {
  const [currentFilters, mode, product, showAddFOBIndicationPanel] =
    useNitroStore((state) => [
      state.currentFilters,
      state.mode,
      state.product,
      state.showAddFOBIndicationPanel,
    ]);

  const [currentIndication, setCurrentIndication] =
    useState<IIndicationHistory | null>(null);
  const [indications, setIndications] = useState<IIndicationHistory[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [state, setState] = useState<"history" | "new">(defaultState);
  useEffect(() => {
    setState(defaultState);
  }, [defaultState]);

  const loadIndicationHistory = async () => {
    setIsLoading(true);
    setCurrentIndication(null);
    setIndications([]);
    try {
      const records = await NitroService.getIndicationHistory({
        mode,
        product,
        locationId: competitor.origin.id,
        shipDate: currentFilters.month,
      });

      if (records.length > 0) {
        setCurrentIndication(records.shift() || null);
        setIndications(records);
      } else {
        setCurrentIndication(null);
        setIndications([]);
      }
    } catch (err) {
      console.log(err);
      setIndications([]);
    }
    setIsLoading(false);
  };

  const isComponentMounted = useIsComponentMounted();
  useEffect(() => {
    if (!isComponentMounted) return;
    loadIndicationHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComponentMounted, currentFilters, competitor]);

  const reloadAll = () => {
    reload();
    loadIndicationHistory();
  };

  const isMobile = useIsMobile(768);
  return (
    <div
      style={{ touchAction: "none" }}
      className={`${
        showAddFOBIndicationPanel ? "hideCompetitor" : "competitor"
      }`}
    >
      <div
        className="competitorBody"
        style={{
          overflowY: "auto",
          maxWidth: "100vw",
          boxShadow:
            "0px 4px 4px 0px rgba(0, 0, 0, 0.25), 0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
          ...(isMobile && { height: "auto" }),
        }}
      >
        {isLoading && <LoadingIcon color="#005CA6" height={100} width={100} />}

        {!isLoading && (
          <>
            <div
              id="indication-panel"
              style={{ position: "absolute", top: 8, right: 8 }}
            >
              <CloseIcon onClick={close} />
            </div>
            {/* HEADER */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "0px 10px",
                paddingTop: 22,
              }}
            >
              <div
                style={{
                  margin: "0px 32px",
                  fontWeight: 500,
                  fontSize: 26,
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <div>{competitor.origin.name}</div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#989898",
                }}
              >
                <div>{`${competitor.distance} Miles`}</div>
                <div
                  style={{
                    height: 5,
                    width: 5,
                    borderRadius: 4,
                    backgroundColor: "#989898",
                  }}
                />
                <div>{`$${competitor.freight}`}</div>
                {mode === "RAIL" && competitor.legs[0].carOwner && (
                  <>
                    <div
                      style={{
                        height: 5,
                        width: 5,
                        borderRadius: 4,
                        backgroundColor: "#989898",
                      }}
                    />
                    <div>{`${competitor.legs[0].carOwner}`}</div>
                  </>
                )}
              </div>
            </div>
            {/* END HEADER */}
            {state !== "new" &&
              !currentIndication &&
              indications.length === 0 && (
                <div
                  style={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    rowGap: 12,
                    paddingTop: "60%",
                  }}
                >
                  <div
                    style={{
                      fontSize: 18,
                      fontStyle: "italic",
                      color: "#595959",
                    }}
                  >
                    No indication history
                  </div>
                  <Button
                    id="addCompetitorIndicationButton"
                    variant="contained"
                    onClick={() => {
                      setState("new");
                    }}
                  >
                    <AddIcon />
                    Add Indication
                  </Button>
                </div>
              )}

            <div className="indications">
              {state !== "new" && currentIndication && (
                <CompetitorIndication indication={currentIndication} />
              )}

              {state === "new" && (
                <NewIndication
                  newIndication={newIndication}
                  competitor={competitor}
                  reload={reloadAll}
                  close={() => setState("history")}
                />
              )}

              {currentIndication && state === "history" && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    id="addCompetitorIndicationHistoryButton"
                    variant="contained"
                    onClick={() => {
                      setState("new");
                    }}
                  >
                    <AddIcon />
                    Add Indication
                  </Button>
                </div>
              )}

              {currentIndication && state === "history" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    paddingTop: 10,
                  }}
                >
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 500,
                      paddingBottom: 10,
                    }}
                  >
                    Indication History
                  </div>
                  <div
                    style={{
                      flexGrow: 1,
                      flexBasis: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 20,
                      paddingBottom: 30,
                    }}
                  >
                    {(!indications || indications.length === 0) && (
                      <div
                        style={{
                          paddingTop: 20,
                          fontStyle: "italic",
                          color: "#595959",
                          fontSize: 18,
                          textAlign: "center",
                        }}
                      >
                        No indication history
                      </div>
                    )}
                    {indications.map((i) => (
                      <CompetitorIndication indication={i} minimize />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Competitor;
